import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectComponentsProvider } from '@stripe/react-connect-js';
import { getAuthToken, invalidateTokenCache } from '../../utils/auth';
import { getStripeConnectPublishableKeyForDashboard } from '../../utils/stripeConnectPublishableKey';
import { getStripeAccountId, hasStripeAccountConflict, paymentOrganizationKey, paymentProcessor } from '../../utils/paymentReadiness';
import { accountsClient } from '../../lib/trpc/accounts';
import { requestStripeSession } from './stripeSessionRequest';

const StripeSessionContext = createContext(null);
export const useStripeSession = () => useContext(StripeSessionContext);

function logoutSession(instance) {
  let timer;
  const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve(false), 8000); });
  let logout;
  try { logout = Promise.resolve(instance.logout()).then(() => true, () => false); }
  catch { logout = Promise.resolve(false); }
  return Promise.race([logout, timeout]).finally(() => clearTimeout(timer));
}

export function useStripeSessionLifecycle({ merchant, userId, activeOrgId, canManage, permissionScope = '', unsupportedBrowser = false }) {
  const [retryKey, setRetryKey] = useState(0);
  const [session, setSession] = useState(null);
  const cleanupRef = useRef(Promise.resolve(true));
  const accountId = getStripeAccountId(merchant);
  const processor = paymentProcessor(merchant);
  const orgId = paymentOrganizationKey(merchant);
  const publishableKey = getStripeConnectPublishableKeyForDashboard();
  const conflict = hasStripeAccountConflict(merchant);
  const activeOrgMatches = !activeOrgId || !merchant?.clerkOrganizationId || activeOrgId === merchant.clerkOrganizationId;
  const enabled = Boolean(userId && orgId && accountId && canManage && activeOrgMatches && processor === 'STRIPE');
  const scope = JSON.stringify([userId, activeOrgId, orgId, accountId, processor, merchant?.stripeDetails?.accountApiVersion, publishableKey, canManage, permissionScope, conflict, unsupportedBrowser]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const retry = useCallback(() => setRetryKey((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let instance;
    const active = () => !controller.signal.aborted && currentScope.current === scope;
    const fail = (error) => { if (active()) { setSession({ scope, instance: null, error }); controller.abort(); } };
    setSession(null);
    if (!enabled) return () => controller.abort();
    if (conflict || unsupportedBrowser || !publishableKey) {
      fail(new Error(conflict ? 'Your Stripe account needs reconciliation. Contact support.' : unsupportedBrowser ? 'Open this dashboard in Safari or Chrome to securely connect Stripe.' : 'Stripe is not configured for this dashboard. Contact support.'));
      return () => controller.abort();
    }
    const initialize = async () => {
      // Finish logging out the previous authenticated scope before creating another.
      const loggedOut = await cleanupRef.current;
      if (!active()) return;
      if (!loggedOut) {
        fail(new Error('The previous Stripe session could not close. Reload this page before continuing with this organization.'));
        return;
      }
      const styles = getComputedStyle(document.documentElement);
      const color = (name) => styles.getPropertyValue(name).trim();
      try {
        instance = loadConnectAndInitialize({
          publishableKey,
          fetchClientSecret: async () => {
            try {
              return await requestStripeSession({
                getToken: getAuthToken,
                invalidateToken: invalidateTokenCache,
                expectedStripeAccountId: accountId,
                signal: controller.signal,
                request: ({ authorization, expectedStripeAccountId, signal }) =>
                  accountsClient.request('POST /stripe/management-session', {
                    authorization,
                    input: { expectedStripeAccountId },
                    signal,
                  }),
              });
            } catch (error) { fail(error); throw error; }
          },
          appearance: { overlays: 'dialog', variables: { colorPrimary: color('--sage'), colorText: color('--ink'), colorSecondaryText: color('--ink-secondary'), colorBorder: color('--edge-input'), buttonPrimaryColorBackground: color('--sage'), buttonPrimaryColorText: '#FFFFFF', fontFamily: 'Inter, system-ui, sans-serif', borderRadius: '6px', fontSizeBase: '14px', spacingUnit: '8px' } },
        });
        if (active()) setSession({ scope, instance, error: null });
      } catch (error) { fail(error); }
    };
    void initialize();
    return () => {
      controller.abort();
      if (instance?.logout) {
        cleanupRef.current = logoutSession(instance);
      }
    };
  }, [scope, enabled, retryKey, conflict, unsupportedBrowser, publishableKey, accountId]);

  const unavailable = !accountId && userId && orgId && canManage && (!processor || processor === 'STRIPE');
  return useMemo(() => ({ instance: session?.scope === scope ? session.instance : null, error: session?.scope === scope ? session.error : unavailable ? new Error('Your Stripe account is not available yet. Try again shortly, or contact support if this continues.') : null, retry, scope, generation: retryKey, enabled }), [session, scope, retry, retryKey, enabled, unavailable]);
}

/** Stable ancestry preserves open modals and drafts when payment approval changes. */
export function StripeSessionProvider({ session, children }) {
  return <StripeSessionContext.Provider value={session}>
    <ConnectComponentsProvider connectInstance={session.instance}>{children}</ConnectComponentsProvider>
  </StripeSessionContext.Provider>;
}
